import { inject, Injectable, makeStateKey, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { Router } from '@angular/router';

import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  startWith,
  Subject,
  Subscription,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { Store } from '@ngrx/store';

import { StateTransferService } from '@fcom/core/services/state-transfer/state-transfer.service';
import { CMSDiscountCode } from '@fcom/core/services/cms/cms-data.service';
import { MonitoringLogger } from '@fcom/core/services/monitoring/monitoring-logger';
import { AvailabilityEntry } from '@fcom/dapi/api/models/availability-entry';
import { Duration } from '@fcom/dapi/api/models/duration';
import { FinnairBoundItem } from '@fcom/dapi/api/models/finnair-bound-item';
import { LowestPriceOfPeriod } from '@fcom/dapi/api/models/lowest-price-of-period';
import { finShare } from '@fcom/rx';
import {
  AppState,
  GlobalBookingTravelClass,
  isPresent,
  LocalDate,
  mapErrorForMonitoring,
  unsubscribe,
} from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import { isFlight } from '@fcom/common-booking/utils/flight.utils';
import { DatePickerPrices } from '@fcom/common/interfaces/price-calendar.interface';
import { ElementActions, ElementTypes, GaContext, SearchType } from '@fcom/common/interfaces/gtm.interface';
import { Amount } from '@fcom/dapi/interfaces/dapi.interface';
import { Location } from '@fcom/core-api/interfaces/location-routecff.interface';
import { CmsTripTypeMap } from '@fcom/core/utils/tripType.utils';
import { TripType } from '@fcom/core/interfaces/triptype.interface';

import {
  CalendarPrices,
  InstantSearchBaseParams,
  InstantSearchFollowingMonthParams,
  InstantSearchFullYearParams,
  PriceCalendarCTAParams,
  PriceCalendarParams,
  PriceCalendarWithPricesParams,
} from '../../interfaces/price-calendar.interface';
import { DirectFlightFrequency } from '../../interfaces/timetable-search.interface';
import { GlobalBookingTripDates } from '../../interfaces/global-booking-trip-dates.interface';
import { HistogramBar } from '../../interfaces/histogram.interface';
import {
  createHistogramData,
  getBookingWidgetCabinClass,
  getLowestCalendarPrice,
  getStartingFromPrice,
  isPriceCalendarWithPricesParams,
  mapInstantSearchPricesToCalendarPrices,
  priceCalendarBaseParamsAreValid,
  priceCalendarPriceParamsAreValid,
} from '../../utils/price-calendar.utils';
import * as GlobalBookingActions from '../../store/actions/global-booking.actions';
import { selectGlobalBookingDiscountCode } from '../../store/selectors/global-booking.selectors';
import { pricesForPeriodPendingSelector } from '../../store/selectors/instant-search.selectors';
import { GlobalBookingFlight } from '../../store/store.interface';
import { GtmService } from '../../gtm/services/gtm.service';
import { createDeeplinkPathFromFlightSearchParams } from '../../utils/flight-search.utils';
import { getDirectFlightFrequency } from '../../utils/timetable-search.utils';
import { TimetableSearchService } from '../timetable/timetable-search.service';
import { CommonInstantSearchService } from '../common-instantsearch/common-instantsearch.service';

const DAYS_YEAR = 360;
export const DAYS_MONTH = 30;

export interface ScheduleBoundsState {
  loading: boolean;
  bounds: FinnairBoundItem[];
}

export interface DestinationRouteInfo {
  tripType: TripType;
  startingFromPrice?: Amount;
  duration?: Duration;
  airportCodes: string[];
  isDirect: boolean;
  directFrequency?: DirectFlightFrequency;
}

export type DestinationTimetableInfo = Pick<
  DestinationRouteInfo,
  'duration' | 'airportCodes' | 'isDirect' | 'directFrequency'
>;

interface FullYearPricesResult {
  fullYear: CalendarPrices;
  histogram: HistogramBar[];
  availability?: AvailabilityEntry[];
  failed?: boolean;
}

const EMPTY_FULL_YEAR_RESULT: Required<Omit<FullYearPricesResult, 'failed'>> = {
  fullYear: {},
  histogram: [],
  availability: [],
};

@Injectable()
export class PriceCalendarService implements OnDestroy {
  private languageService = inject(LanguageService);
  private commonInstantSearchService = inject(CommonInstantSearchService);
  private monitoringLogger = inject(MonitoringLogger);
  private appStore$ = inject<Store<AppState>>(Store);
  private gtmService = inject(GtmService);
  private router = inject(Router);
  private timetableSearchService = inject(TimetableSearchService);
  private stateTransferService = inject(StateTransferService);
  private readonly isServer = isPlatformServer(inject(PLATFORM_ID));

  private readonly _startingFromPrice$: BehaviorSubject<Amount> = new BehaviorSubject(undefined);
  private readonly _fullYearPrices$: BehaviorSubject<DatePickerPrices> = new BehaviorSubject({
    calendar: undefined,
    histogram: undefined,
  });
  private readonly _calendarPrices$: Observable<DatePickerPrices> = of(undefined);
  private _followingMonthPrices$: BehaviorSubject<CalendarPrices> = new BehaviorSubject({});
  private _travelDates$ = new BehaviorSubject<GlobalBookingTripDates>({
    departureDate: undefined,
    returnDate: undefined,
  });

  private _params$: BehaviorSubject<PriceCalendarParams> = new BehaviorSubject<PriceCalendarParams>(undefined);

  private _isLoading$: Observable<boolean>;

  private _discountCode$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  private _availability$: BehaviorSubject<AvailabilityEntry[]> = new BehaviorSubject<AvailabilityEntry[]>([]);

  private _scheduleBounds$: Observable<ScheduleBoundsState>;
  private _returnScheduleBounds$: Observable<ScheduleBoundsState>;

  private subscriptions = new Subscription();

  private knownNoPricesKey: string | null = null;
  private readonly serverFullYearResults = new Map<string, FullYearPricesResult>();
  service: Observable<PriceCalendarWithPricesParams>;
  calendarOpen$ = new Subject<PriceCalendarParams | null>();

  constructor() {
    this._calendarPrices$ = combineLatest([
      this._params$.pipe(filter(isPresent)),
      this._fullYearPrices$,
      this._followingMonthPrices$,
      this._travelDates$,
    ]).pipe(
      map(([{ tripType }, fullYearPrices, followingMonth, { departureDate, returnDate }]) => {
        const oneway = tripType === TripType.ONEWAY;
        const onlyDepartureSelectedAndNotOneway = isPresent(departureDate) && !oneway && !isPresent(returnDate);
        const prices = onlyDepartureSelectedAndNotOneway ? followingMonth : fullYearPrices?.calendar;

        const pricesWithSelections =
          departureDate && returnDate ? { ...prices, [returnDate?.id]: followingMonth?.[returnDate?.id] } : prices;
        return { calendar: pricesWithSelections, histogram: fullYearPrices?.histogram };
      }),
      finShare()
    );

    this.subscriptions.add(
      combineLatest([this._calendarPrices$, this._params$.pipe(filter(isPresent)), this._travelDates$])
        .pipe(
          map(([prices, { tripType }, travelDates]) => getStartingFromPrice(prices, tripType, travelDates)),
          startWith(undefined)
        )
        .subscribe((val) => this._startingFromPrice$.next(val))
    );

    const fullYearPricesParams$ = this._params$.pipe(
      filter(isPresent),
      filter((params) => isPriceCalendarWithPricesParams(params)),
      distinctUntilChanged(
        (prev: PriceCalendarWithPricesParams, next: PriceCalendarWithPricesParams) =>
          prev.origin === next.origin &&
          prev.destination === next.destination &&
          prev.tripType === next.tripType &&
          prev.travelClass === next.travelClass &&
          prev.locale === next.locale
      ),
      finShare()
    );

    this.subscriptions.add(
      fullYearPricesParams$
        .pipe(
          switchMap((params: PriceCalendarWithPricesParams) => this.getPricesForFullYear(params)),
          map(({ fullYear, histogram, availability }) => [{ calendar: fullYear, histogram }, availability] as const),
          finShare()
        )
        .subscribe(([val, availability]) => {
          this._fullYearPrices$.next(val);
          this._availability$.next(availability);
        })
    );

    this.subscriptions.add(
      combineLatest([this._params$, this._travelDates$])
        .pipe(
          filter(
            ([params, travelDates]: [PriceCalendarWithPricesParams, GlobalBookingTripDates]) =>
              isPresent(params) && isPresent(travelDates.departureDate?.id)
          ),
          distinctUntilChanged(([prevParams, prevTravelDates], [nextParams, nextTravelDates]) => {
            const departureDateNotChanged = nextTravelDates.departureDate?.id === prevTravelDates.departureDate?.id;
            const locationsNotChanged =
              nextParams.origin === prevParams.origin && nextParams.destination === prevParams.destination;
            const tripTypeNotChanged = prevParams.tripType === nextParams.tripType;
            const travelClassNotChanged = prevParams.travelClass === nextParams.travelClass;
            const localeNotChanged = prevParams.locale === nextParams.locale;
            return (
              departureDateNotChanged &&
              locationsNotChanged &&
              tripTypeNotChanged &&
              travelClassNotChanged &&
              localeNotChanged
            );
          }),
          filter(([params]) => isPriceCalendarWithPricesParams(params)),
          switchMap(([params, travelDates]: [PriceCalendarWithPricesParams, GlobalBookingTripDates]) =>
            this.getPricesForFollowingMonth(params, travelDates.departureDate)
          ),
          finShare()
        )
        .subscribe((value) => this._followingMonthPrices$.next(value))
    );

    this.subscriptions.add(
      this.appStore$
        .select(selectGlobalBookingDiscountCode)
        .pipe(map((discountCode: string) => (isPresent(discountCode) ? discountCode : null)))
        .subscribe((discountCode: string) => {
          this._discountCode$.next(discountCode);
        })
    );

    this._isLoading$ = fullYearPricesParams$.pipe(
      switchMap((params: PriceCalendarWithPricesParams) =>
        this.appStore$.select(pricesForPeriodPendingSelector(this.convertToFullYearParams(params)))
      ),
      startWith(false),
      distinctUntilChanged(),
      finShare()
    );

    this.subscriptions.add(
      this._isLoading$.pipe(filter(Boolean)).subscribe(() => {
        this._availability$.next([]);
      })
    );

    // Timetable bounds per leg. Derived from the same params$/travelDates$ this service already
    // owns, and fetched via the shared instant-search DAPI
    this._scheduleBounds$ = this.buildScheduleBounds$((params, dates) => ({
      origin: params?.origin,
      destination: params?.destination,
      date: dates?.departureDate,
    }));

    this._returnScheduleBounds$ = this.buildScheduleBounds$((params, dates) => ({
      origin: params?.destination,
      destination: params?.origin,
      date: dates?.returnDate,
    }));
  }

  /**
   * Builds a schedule-bounds stream for one leg. `legSelector` picks the origin/destination/date
   * from the current params + travel dates. Emits an empty state until all three are
   * present, then fetches the day's flights and keeps only those departing on the selected date.
   */
  private buildScheduleBounds$(
    legSelector: (
      params: PriceCalendarParams,
      dates: GlobalBookingTripDates
    ) => { origin: string | undefined; destination: string | undefined; date: LocalDate | undefined }
  ): Observable<ScheduleBoundsState> {
    return combineLatest([this._params$, this._travelDates$]).pipe(
      map(([params, dates]) => legSelector(params, dates)),
      distinctUntilChanged(
        (prev, next) =>
          prev.origin === next.origin && prev.destination === next.destination && prev.date?.id === next.date?.id
      ),
      switchMap(({ origin, destination, date }) => {
        if (!isPresent(origin) || !isPresent(destination) || !isPresent(date)) {
          return of<ScheduleBoundsState>({ loading: false, bounds: [] });
        }
        return this.timetableSearchService.getFlights(origin, destination, date, 1).pipe(
          map((flightsByDay): ScheduleBoundsState => {
            return { loading: false, bounds: flightsByDay[date.id] ?? [] };
          }),
          catchError(() => of<ScheduleBoundsState>({ loading: false, bounds: [] })),
          startWith<ScheduleBoundsState>({ loading: true, bounds: [] })
        );
      }),
      finShare()
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  updateParams(params: PriceCalendarParams): void {
    if (isPresent(params) && (priceCalendarPriceParamsAreValid(params) || priceCalendarBaseParamsAreValid(params))) {
      this._params$.next(params);
      if (params.initialTravelDates) {
        this._travelDates$.next(params.initialTravelDates);
      }
    }
  }

  /**
   * Full-year searches for these params resolve to "no prices" without calling the API, until `clearKnownNoPrices`
   * is called. Searches for other params still fetch
   */
  markKnownNoPrices(params: PriceCalendarWithPricesParams): void {
    const key = this.toFullYearRequestKey(params);
    this.knownNoPricesKey = key;

    const currentParams = this._params$.getValue();
    if (
      currentParams &&
      isPriceCalendarWithPricesParams(currentParams) &&
      this.toFullYearRequestKey(currentParams) === key
    ) {
      this._fullYearPrices$.next({
        calendar: EMPTY_FULL_YEAR_RESULT.fullYear,
        histogram: EMPTY_FULL_YEAR_RESULT.histogram,
      });
      this._availability$.next(EMPTY_FULL_YEAR_RESULT.availability);
    }
  }

  clearKnownNoPrices(): void {
    this.knownNoPricesKey = null;
  }

  get params$(): Observable<PriceCalendarParams> {
    return this._params$.asObservable().pipe(finShare());
  }

  get tripType$(): Observable<TripType> {
    return this._params$.pipe(
      filter(isPresent),
      map(({ tripType }) => tripType),
      finShare()
    );
  }

  get travelDates$(): Observable<GlobalBookingTripDates> {
    return this._travelDates$.asObservable().pipe(finShare());
  }

  get travelDatesValues(): GlobalBookingTripDates {
    return this._travelDates$.getValue();
  }

  /** Timetable flights (+ loading) for the selected departure date */
  get scheduleBounds$(): Observable<ScheduleBoundsState> {
    return this._scheduleBounds$;
  }

  /** Timetable flights (+ loading) for the return leg */
  get returnScheduleBounds$(): Observable<ScheduleBoundsState> {
    return this._returnScheduleBounds$;
  }

  get startingFromPrice$(): Observable<Amount> {
    return this._startingFromPrice$.pipe(finShare());
  }

  get fullYearPrices$(): Observable<DatePickerPrices> {
    return this._fullYearPrices$.asObservable().pipe(finShare());
  }

  get calendarPrices$(): Observable<DatePickerPrices> {
    return this._calendarPrices$;
  }

  get isLoading$(): Observable<boolean> {
    return this._isLoading$.pipe(finShare());
  }

  get pricesAvailable$(): Observable<boolean> {
    return combineLatest([this.fullYearPrices$, this.isLoading$]).pipe(
      filter(([{ calendar }, isLoading]) => isPresent(calendar) && !isLoading),
      map(([{ calendar }]) => Object.keys(calendar).length > 0),
      distinctUntilChanged(),
      finShare()
    );
  }

  get pricesAvailableForSelectedDates$(): Observable<boolean> {
    return combineLatest([this.tripType$, this.travelDates$, this.pricesAvailable$, this.startingFromPrice$]).pipe(
      filter(([tripType, travelDates]) => {
        const { departureDate, returnDate } = travelDates;
        if (tripType === TripType.ONEWAY) {
          return isPresent(departureDate);
        }
        if (tripType === TripType.RETURN) {
          return isPresent(departureDate) && isPresent(returnDate);
        }
        return false;
      }),
      map(([, , pricesAvailable, startingFromPrice]) => pricesAvailable && isPresent(startingFromPrice)),
      finShare()
    );
  }

  get followingMonthPrices$(): Observable<CalendarPrices> {
    return this._followingMonthPrices$.asObservable().pipe(finShare());
  }

  get availability$(): Observable<AvailabilityEntry[]> {
    return this._availability$.asObservable().pipe(finShare());
  }

  convertToReturnTrip(): void {
    this._fullYearPrices$.next({ calendar: undefined, histogram: undefined });

    const currentParams = this._params$.getValue();
    this._params$.next({
      ...currentParams,
      tripType: TripType.RETURN,
    });
  }

  getPriceCalendarCTAParams(): Observable<PriceCalendarCTAParams> {
    return this._params$.pipe(
      filter(Boolean),
      take(1),
      map((params) => {
        const { tripType, destination, origin, discountCode } = params;

        const { departureDate, returnDate } = this._travelDates$.getValue();
        const departureFlight: GlobalBookingFlight = {
          origin: { locationCode: origin } as Location,
          destination: { locationCode: destination } as Location,
          departureDate,
        };
        const returnFlight: GlobalBookingFlight = {
          origin: { locationCode: destination } as Location,
          destination: { locationCode: origin } as Location,
          departureDate: returnDate,
        };

        const flights: GlobalBookingFlight[] =
          tripType === TripType.RETURN ? [departureFlight, returnFlight] : [departureFlight];

        return {
          flights,
          tripType: tripType,
          ...(isPriceCalendarWithPricesParams(params) && {
            paxAmount: params.paxAmount,
            travelClass: getBookingWidgetCabinClass(params.travelClass, false, false, tripType === TripType.MULTICITY),
          }),
          discountCode,
        };
      })
    );
  }

  getPricesForFullYear(params: PriceCalendarWithPricesParams): Observable<FullYearPricesResult> {
    const fullYearParams = this.convertToFullYearParams(params);
    const key = JSON.stringify(fullYearParams);

    if (this.knownNoPricesKey === key) {
      return of(EMPTY_FULL_YEAR_RESULT);
    }

    if (this.isServer && this.serverFullYearResults.has(key)) {
      return of(this.serverFullYearResults.get(key));
    }

    return this.commonInstantSearchService.pricesForPeriod(fullYearParams).pipe(
      withLatestFrom(this.languageService.translate<Record<string, string>>('date')),
      map(([response, dateTranslations]: [LowestPriceOfPeriod, Record<string, string>]) => {
        const fullYear = mapInstantSearchPricesToCalendarPrices(response);
        const histogram = createHistogramData(response, Object.keys(fullYear), dateTranslations);

        return {
          fullYear,
          histogram,
          availability: response?.availability,
        };
      }),
      catchError((err: unknown) => {
        this.monitoringLogger.error('Error finding prices for period', {
          error: mapErrorForMonitoring(err),
        });
        return of({
          fullYear: {},
          histogram: [],
          failed: true,
        });
      }),
      tap((result: FullYearPricesResult) => {
        if (this.isServer && !result.failed) {
          this.serverFullYearResults.set(key, result);
        }
      }),
      finShare()
    );
  }

  getStartingFromPrice(params: PriceCalendarWithPricesParams): Observable<Amount | undefined> {
    if (!params.origin || !params.destination) {
      return of(undefined);
    }

    return this.getPricesForFullYear(params).pipe(map(({ fullYear }) => getLowestCalendarPrice(fullYear)));
  }

  getDestinationTimetableInfo(origin: string, destination: string): Observable<DestinationTimetableInfo> {
    if (!origin || !destination) {
      return of({ airportCodes: [], isDirect: false });
    }

    const stateKey = makeStateKey<DestinationTimetableInfo>(`c2c-tt-${origin}-${destination}-${LocalDate.now().id}`);

    return this.stateTransferService
      .wrapToStateCache(stateKey, () => {
        return this.getRouteTimetable(origin, destination).pipe(
          map((bounds) => {
            // Includes direct and w/ technical stop
            const directBounds = bounds.filter((bound) => bound.itinerary.filter(isFlight).length === 1);
            const isDirect = directBounds.length > 0;
            const relevantBounds = isDirect ? directBounds : bounds;
            return {
              duration: this.getShortestBound(relevantBounds)?.duration,
              airportCodes: [...new Set(relevantBounds.map((bound) => bound.arrival.locationCode))],
              isDirect,
              directFrequency: getDirectFlightFrequency(directBounds.length, DAYS_MONTH),
            };
          })
        );
      })
      .pipe(
        catchError(() => {
          return of<DestinationTimetableInfo>({
            duration: undefined,
            airportCodes: [],
            isDirect: false,
          });
        }),
        finShare()
      );
  }

  private getRouteTimetable(origin: string, destination: string): Observable<FinnairBoundItem[]> {
    return this.timetableSearchService.getFlights(origin, destination, LocalDate.now(), DAYS_MONTH).pipe(
      map((flightsByDay) => {
        // Partial results must not be written to TransferState.
        if (Object.keys(flightsByDay).length < DAYS_MONTH) {
          throw new Error('Incomplete route timetable');
        }
        return Object.values(flightsByDay).flat();
      })
    );
  }

  private getShortestBound(bounds: FinnairBoundItem[]): FinnairBoundItem | undefined {
    if (bounds.length === 0) {
      return undefined;
    }
    return bounds.reduce((shortest, bound) => {
      return bound.duration.milliseconds < shortest.duration.milliseconds ? bound : shortest;
    });
  }

  getPricesForFollowingMonth(
    params: PriceCalendarWithPricesParams,
    departureDate: LocalDate
  ): Observable<CalendarPrices> {
    const followingMonthParams = this.convertToFollowingMonthParams(params, departureDate);
    return this.commonInstantSearchService.fixedDeparture(followingMonthParams).pipe(
      map((response: LowestPriceOfPeriod) => mapInstantSearchPricesToCalendarPrices(response)),
      catchError(() => of({})),
      finShare()
    );
  }

  updateCalendarDates(calendarDates: GlobalBookingTripDates): void {
    this._travelDates$.next(calendarDates);
  }

  openPriceCalendar(
    originLocationCode: string,
    destinationLocationCode: string,
    tripType: TripType,
    travelClass: GlobalBookingTravelClass,
    destinationTitle: string,
    discountCode?: CMSDiscountCode,
    shouldTrackEvent = true
  ): void {
    this.calendarOpen$.next({
      origin: originLocationCode,
      destination: destinationLocationCode,
      tripType,
      travelClass,
      paxAmount: {
        adults: 1,
        c15s: 0,
        children: 0,
        infants: 0,
      },
      discountCode,
    });

    if (shouldTrackEvent) {
      this.gtmService.trackElement(
        `tile-${destinationTitle.replace(/ /g, '_')}-${travelClass}-${CmsTripTypeMap[tripType]}`,
        `${GaContext.DESTINATION_MAIN_PAGE}_open-dates`,
        ElementTypes.LIST_ITEM,
        undefined,
        ElementActions.CLICK
      );
    }
  }

  priceCalendarCTAClicked(params: PriceCalendarCTAParams, searchType?: SearchType): void {
    const discountCode = params.discountCode?.code ?? this._discountCode$.getValue();

    this.prefillBookingWidget(
      params.travelClass,
      { departureDate: params.flights[0].departureDate, returnDate: params.flights[1]?.departureDate },
      params.tripType,
      params.flights[0].destination.locationCode,
      params.flights[0].origin.locationCode,
      discountCode
    );

    const deeplink = createDeeplinkPathFromFlightSearchParams({
      flights: params.flights,
      tripType: params.tripType,
      paxAmount: params.paxAmount,
      travelClass: params.travelClass,
      locale: this.languageService.langValue,
      promoCode: discountCode,
    });
    this.router.navigateByUrl(deeplink, { state: { searchType } });
  }

  closePriceCalendar(): void {
    this.calendarOpen$.next(null);
  }

  prefillBookingWidget(
    travelClass: GlobalBookingTravelClass,
    selectedDates: {
      departureDate: LocalDate | undefined;
      returnDate: LocalDate | undefined;
    },
    tripType: TripType,
    destinationLocationCode: string,
    selectedOriginLocationCode: string,
    discountCode?: string
  ): void {
    const departureFlight = {
      origin: { locationCode: selectedOriginLocationCode } as Location,
      destination: { locationCode: destinationLocationCode } as Location,
      departureDate: selectedDates.departureDate,
    };
    const returnFlight = {
      origin: { locationCode: destinationLocationCode } as Location,
      destination: { locationCode: selectedOriginLocationCode } as Location,
      departureDate: selectedDates.returnDate,
    };

    const flights: GlobalBookingFlight[] =
      tripType === TripType.RETURN ? [departureFlight, returnFlight] : [departureFlight];

    this.appStore$.dispatch(GlobalBookingActions.setFlights({ flights }));
    this.appStore$.dispatch(
      GlobalBookingActions.setTravelClass({
        travelClass: getBookingWidgetCabinClass(travelClass, false, false, tripType === TripType.MULTICITY),
      })
    );
    this.appStore$.dispatch(GlobalBookingActions.setOrigin({ origin: selectedOriginLocationCode }));
    this.appStore$.dispatch(GlobalBookingActions.setTripType({ tripType }));

    if (discountCode) {
      this.appStore$.dispatch(GlobalBookingActions.setDiscountCode({ discountCode }));
    }
  }

  private getCommonParams({
    origin,
    destination,
    paxAmount,
    travelClass,
  }: PriceCalendarWithPricesParams): Omit<InstantSearchBaseParams, 'numberOfDays'> {
    return {
      departureLocationCode: origin,
      destinationLocationCode: destination,
      adults: paxAmount.adults + paxAmount.c15s,
      children: paxAmount.children,
      infants: paxAmount.infants,
      locale: this.languageService.localeValue,
      travelClass: travelClass === GlobalBookingTravelClass.MIXED ? null : travelClass,
    };
  }

  private convertToFollowingMonthParams(
    params: PriceCalendarWithPricesParams,
    departureDate: LocalDate
  ): InstantSearchFollowingMonthParams {
    const commonParams = this.getCommonParams(params);
    return { ...commonParams, numberOfDays: DAYS_MONTH, departureDate: departureDate.id };
  }

  private convertToFullYearParams(params: PriceCalendarWithPricesParams): InstantSearchFullYearParams {
    const commonParams = this.getCommonParams(params);
    return {
      ...commonParams,
      oneway: params.tripType === TripType.ONEWAY,
      startDate: LocalDate.now().id,
      numberOfDays: DAYS_YEAR,
    };
  }

  private toFullYearRequestKey(params: PriceCalendarWithPricesParams): string {
    return JSON.stringify(this.convertToFullYearParams(params));
  }
}
